// AdminVisits.js
import React, { useEffect, useMemo, useState } from 'react';
import API from './api';
import { MapPin, Building2, Home } from 'lucide-react';
import { FaBookOpen } from 'react-icons/fa';
import StudentDetailsModal from './StudentDetailsModal';
import { MapModal } from './MapModal';

import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Ikonica za marker (ista kao u AdminVisitsToday)
const teacherIcon = new L.Icon({
  iconUrl: 'https://cdn.jsdelivr.net/npm/leaflet@1.7/dist/images/marker-icon.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

// Jednostavan modal za single-location mapu (za G-sign), isti princip kao u AdminVisitsToday
function SimpleMapModal({ open, onClose, position, label }) {
  if (!open || !position?.lat || !position?.lon) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-lg relative p-4">
        <button
          className="absolute top-2 right-2 text-gray-400 hover:text-red-600 text-xl"
          onClick={onClose}
        >
          ×
        </button>
        <h3 className="text-sm mb-2">{label}</h3>
        <div className="h-80 w-full">
          <MapContainer
            center={[position.lat, position.lon]}
            zoom={15}
            scrollWheelZoom={false}
            className="h-full w-full rounded"
          >
            <TileLayer attribution="© OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <Marker position={[position.lat, position.lon]} icon={teacherIcon}>
              <Popup>{label}</Popup>
            </Marker>
          </MapContainer>
        </div>
      </div>
    </div>
  );
}

// Work location modal (map) – isti princip kao u MyVisits
function WorkLocationModal({ open, onClose, title, addressLine, dest, teacher }) {
  if (!open) return null;

  const hasCoords =
    (dest?.lat != null && dest?.lon != null) ||
    (dest?.latitude != null && dest?.longitude != null);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="min-w-0">
            <h3 className="text-lg font-bold truncate">{title || 'Work location'}</h3>
            {addressLine ? <p className="text-sm text-gray-600 mt-1">{addressLine}</p> : null}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800" title="Close">
            ✕
          </button>
        </div>

        {!hasCoords ? (
          <div className="p-4 text-center text-gray-500 text-sm">Location coordinates are missing.</div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <MapModal open={open} student={dest} teacher={teacher} onClose={onClose} />
          </div>
        )}

        <div className="flex justify-end mt-4">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// helpers
const pad2 = (n) => String(n).padStart(2, '0');
const fmt = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const parseYMDLocal = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
};
const startOfLocalDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

function lastNDaysRange(n, base = new Date()) {
  const baseDate = typeof base === 'string' ? parseYMDLocal(base) : base;
  const end = startOfLocalDay(baseDate);
  const start = new Date(end);
  start.setDate(start.getDate() - n);
  return { from: fmt(start), to: fmt(end) };
}

function addMinutesHHMM(hhmm, mins) {
  if (!hhmm) return null;
  const s = String(hhmm).trim().slice(0, 5);
  const [h, m] = s.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  const dt = new Date(2000, 0, 1, h || 0, m || 0, 0, 0);
  dt.setMinutes(dt.getMinutes() + (mins || 0));
  const hh = String(dt.getHours()).padStart(2, '0');
  const mm = String(dt.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function parseDateStr(v) {
  const s = String(v || '');
  const d = s.slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : '';
}

function parseHHMM(val) {
  const s = String(val || '').trim();
  const m = s.match(/(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '';
}

function minutesFromHHMM(hhmm) {
  const t = parseHHMM(hhmm);
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

function itemTimeHHMM(v) {
  return parseHHMM(v?.scheduled_time) || parseHHMM(v?.from_hhmm) || parseHHMM(v?.scheduled_at) || '';
}

function itemSortValue(v) {
  const d = parseDateStr(v?.scheduled_at);
  const hhmm = itemTimeHHMM(v);
  const mins = minutesFromHHMM(hhmm) ?? 0;

  const y = Number(d.slice(0, 4)) || 0;
  const mo = Number(d.slice(5, 7)) || 0;
  const da = Number(d.slice(8, 10)) || 0;
  const dayKey = y * 372 + mo * 31 + da;

  return dayKey * 1440 + mins;
}

function isWorkItem(v) {
  return v && (v.__kind === 'work' || v._is_work === true);
}

function sortUnified(a, b) {
  const av = itemSortValue(a);
  const bv = itemSortValue(b);
  if (av !== bv) return av - bv;

  const ak = isWorkItem(a) ? 1 : 0;
  const bk = isWorkItem(b) ? 1 : 0;
  if (ak !== bk) return ak - bk;

  // stabilize
  const aid = Number(a?.id || a?.work_id || a?.visit_id || 0);
  const bid = Number(b?.id || b?.work_id || b?.visit_id || 0);
  return aid - bid;
}

function groupByDateUnified(items) {
  const acc = {};
  for (const v of items || []) {
    const date = parseDateStr(v?.scheduled_at);
    if (!date) continue;
    if (!acc[date]) acc[date] = [];
    acc[date].push(v);
  }
  Object.keys(acc).forEach((d) => acc[d].sort(sortUnified));
  return acc;
}

function StatusBadge({ status, children }) {
  let color = 'bg-gray-200 text-gray-700';
  if (status === 'pending') color = 'bg-yellow-100 text-yellow-800';
  if (status === 'accepted') color = 'bg-green-100 text-green-700';
  if (status === 'rejected') color = 'bg-red-100 text-red-700';
  if (status === 'draft') color = 'bg-blue-100 text-blue-700';
  if (status === 'complete') color = 'bg-green-200 text-green-900';
  if (status === 'canceled') color = 'bg-purple-200 text-purple-700';
  return (
    <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${color}`}>
      {children || status}
    </span>
  );
}

/** Supervised visit bedž – tirkizno plavo-zelen, isti kao u AdminVisitsToday */
function SupervisedBadge() {
  return (
    <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-teal-100 text-teal-800 border border-teal-200">
      Supervised visit
    </span>
  );
}

/**
 * Disciplinarni bedž (EI / PI / ABA / OT / PT…)
 */
function ProgramBadge({ program_type, discipline_code }) {
  const raw = (discipline_code || program_type || '').toString().trim();
  if (!raw) return null;

  const code = raw.replace(/^PROG_/i, '').toUpperCase();

  let cls = 'px-2 py-0.5 rounded text-xs';
  let extra = '';
  let label = code;

  if (code === 'PI') {
    extra = ' bg-orange-100 text-orange-800 border border-orange-200';
    label = 'PI';
  } else if (code === 'EI') {
    extra = ' bg-blue-100 text-blue-800 border border-blue-200';
    label = 'EI';
  } else if (code === 'ABA') {
    extra = ' bg-purple-100 text-purple-800 border border-purple-200';
    label = 'ABA';
  } else if (code === 'OT') {
    extra = ' bg-teal-100 text-teal-800 border border-teal-200';
    label = 'OT';
  } else if (code === 'PT') {
    extra = ' bg-emerald-100 text-emerald-800 border border-emerald-200';
    label = 'PT';
  } else if (code === 'ST') {
    extra = ' bg-green-100 text-green-800 border border-green-200';
    label = 'ST';
  } else {
    extra = ' border border-gray-400 text-gray-700 bg-gray-50';
    label = code;
  }

  return <span className={cls + extra}>{label}</span>;
}

/**
 * Event bedž – žuti, odvojen od discipline
 */
function EventBadge({ isEvent }) {
  if (!isEvent) return null;
  return (
    <span className="px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800 border border-yellow-300">
      EVENT
    </span>
  );
}

/**
 * VisitTimeType:
 * - prikazuje samo FROM–TO vreme
 * - TO: event_end_time (ako postoji) ili FROM + 45min
 */
function VisitTimeType({ scheduled_at, scheduled_time, event_end_time }) {
  const from = scheduled_time || scheduled_at?.slice(11, 16) || '';
  let to = null;

  if (event_end_time) {
    to = String(event_end_time).slice(0, 5);
  } else if (from) {
    to = addMinutesHHMM(from, 45);
  }

  return (
    <div className="flex flex-col items-start">
      <span className="font-mono text-base">{from && to ? `${from}–${to}` : from || '-'}</span>
    </div>
  );
}

function compareDate(a, b) {
  return a.localeCompare(b);
}

// helper – prepoznaj EVENT posete
const isEventVisit = (v) =>
  v.type === 'event' || v.visit_type === 'event' || v.is_event === 1 || v.is_event === true;

// CSV export
function exportToCSV(visits) {
  if (!visits.length) return;
  const headers = Object.keys(visits[0]);
  const csvRows = [
    headers.join(','),
    ...visits.map((v) =>
      headers.map((h) => `"${(v[h] ?? '').toString().replace(/"/g, '""')}"`).join(',')
    ),
  ];
  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'visits.csv';
  a.click();
  window.URL.revokeObjectURL(url);
}

function buildClinicDestFromLocationRow(loc) {
  if (!loc) return null;

  const parts = [loc.street, loc.city, loc.state, loc.zip, loc.country]
    .map((x) => String(x || '').trim())
    .filter(Boolean);

  const addressLine = parts.join(', ');

  const lat = loc.latitude != null ? Number(loc.latitude) : null;
  const lon = loc.longitude != null ? Number(loc.longitude) : null;

  return {
    id: loc.id,
    name: (loc.name || 'Clinic').trim(),
    address: addressLine || (loc.geocoded_label ? String(loc.geocoded_label) : ''),
    geocoded_label: loc.geocoded_label || null,
    lat,
    lon,
    latitude: lat,
    longitude: lon,
  };
}

function getClinicNameForVisit(v, orgLocationsMap) {
  const mode = String(v?.location_mode || '').toLowerCase();
  if (mode !== 'at_clinic') return '';
  const id = Number(v?.org_location_id);
  if (!id) return '';
  const loc = orgLocationsMap?.[id];
  return (loc?.name || '').trim();
}

function getOrgLocNameForWork(w, orgLocationsMap) {
  if (!w) return '';
  if (String(w.location_mode || '') !== 'org_location') return '';
  const id = Number(w.org_location_id);
  if (!id) return '';
  const loc = orgLocationsMap?.[id];
  return (loc?.name || '').trim();
}

function normalizeTeacherWorkToUnified(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return list.map((tw) => {
    const from = tw.from_hhmm || parseHHMM(tw.scheduled_at) || '';
    const to = tw.to_hhmm || tw.end_hhmm || '';

    return {
      ...tw,
      __kind: 'work',
      _is_work: true,

      // unify for time display
      scheduled_time: from,
      event_end_time: to ? `${String(to).slice(0, 5)}:00` : null,

      // unify id key for React
      work_id: tw.id,

      // keep canceled reason as-is (teacher_work.canceled_reason)
      canceled_reason: tw.canceled_reason || tw.cancel_reason || tw.cancelled_reason || null,
    };
  });
}

function AdminVisits() {
  const [visits, setVisits] = useState([]);
  const [teacherWork, setTeacherWork] = useState([]);

  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);

  const [orgLocationsMap, setOrgLocationsMap] = useState({});

  const [filters, setFilters] = useState(() => {
    const { from, to } = lastNDaysRange(15);
    return {
      dateFrom: from,
      dateTo: to,
      teacherId: '',
      studentId: '',
      status: '',
      search: '',
    };
  });

  const [serverDateStr, setServerDateStr] = useState(null);

  const [studentDetailsModal, setStudentDetailsModal] = useState({ open: false, student: null });
  const [mapModal, setMapModal] = useState({ open: false, student: null, teacher: null });

  const [showNotesHistory, setShowNotesHistory] = useState(false);
  const [notesHistory, setNotesHistory] = useState([]);
  const [notesHistoryLoading, setNotesHistoryLoading] = useState(false);
  const [notesHistoryStudent, setNotesHistoryStudent] = useState(null);

  // ✅ Work notes modal (title + notes)
  const [workNotesModal, setWorkNotesModal] = useState({
    open: false,
    teacherName: '',
    workType: '',
    workTitle: '',
    notes: '',
    scheduledAt: '',
  });

  const [gsignLog, setGsignLog] = useState({});
  const [gsignMapModal, setGsignMapModal] = useState({ open: false, position: null, date: null });

  const [workLocModal, setWorkLocModal] = useState({
    open: false,
    title: '',
    addressLine: '',
    dest: null,
    teacher: null,
  });

  const round2 = (num) => Math.round(Number(num) * 100) / 100;

  const teachersMap = useMemo(() => {
    const m = {};
    (teachers || []).forEach((t) => {
      if (t?.id) m[Number(t.id)] = t;
    });
    return m;
  }, [teachers]);

  const resolveTeacherName = (maybeName, teacherId) => {
    const n = String(maybeName || '').trim();
    if (n) return n;
    const tid = Number(teacherId);
    if (tid && teachersMap[tid]?.name) return String(teachersMap[tid].name).trim();
    return tid ? `#${tid}` : '';
  };

  const openStudentModal = async (studentId) => {
    try {
      const res = await API.get(`/students/${studentId}/full`);
      setStudentDetailsModal({ open: true, student: res.data });
    } catch {
      alert('Failed to load student details');
    }
  };

  // visit map (clinic override)
  const openMapModalVisit = async (visit) => {
    try {
      const res = await API.get('/visit-map-locations', {
        params: { student_id: visit.student_id, teacher_id: visit.teacher_id },
      });

      let studentObj = res.data.student || null;
      const teacherObj = res.data.teacher || null;

      const mode = String(visit.location_mode || 'at_home').toLowerCase();
      const isClinic = mode === 'at_clinic';

      // override destinacije na kliniku (org_location_id -> organization_locations)
      if (isClinic && visit.org_location_id) {
        const loc = orgLocationsMap?.[Number(visit.org_location_id)];
        const clinicDest = buildClinicDestFromLocationRow(loc);
        if (clinicDest?.lat != null && clinicDest?.lon != null) {
          studentObj = clinicDest;
        }
      }

      setMapModal({ open: true, student: studentObj, teacher: teacherObj });
    } catch {
      alert('Failed to fetch map data');
    }
  };

  // ✅ work notes modal
  const openWorkNotesModal = (w) => {
    const teacherName = resolveTeacherName(w.teacher_name || w.teacher_full_name || w.teacher, w.teacher_id);
    const workType = String(w.work_type || 'other');
    setWorkNotesModal({
      open: true,
      teacherName,
      workType,
      workTitle: String(w.title || '').trim(),
      notes: String(w.notes || '').trim(),
      scheduledAt: String(w.scheduled_at || ''),
    });
  };

  // work location modal
  const openWorkLocationModal = (w) => {
    const workType = String(w.work_type || 'other');

    let locationName = '';
    let addressLine = '';
    let lat = null;
    let lon = null;

    if (w.location_mode === 'org_location') {
      // prefer fields from API, but fallback to orgLocationsMap by org_location_id
      const fallbackLoc = w.org_location_id ? orgLocationsMap?.[Number(w.org_location_id)] : null;

      locationName =
        String(w.org_location_name || '').trim() ||
        String(fallbackLoc?.name || '').trim();

      const parts = [
        w.org_location_street ?? fallbackLoc?.street,
        w.org_location_city ?? fallbackLoc?.city,
        w.org_location_state ?? fallbackLoc?.state,
        w.org_location_zip ?? fallbackLoc?.zip,
        w.org_location_country ?? fallbackLoc?.country,
      ]
        .map((x) => String(x || '').trim())
        .filter(Boolean);
      addressLine = parts.join(', ');

      const latSrc = w.org_location_latitude ?? fallbackLoc?.latitude;
      const lonSrc = w.org_location_longitude ?? fallbackLoc?.longitude;

      lat = latSrc != null ? Number(latSrc) : null;
      lon = lonSrc != null ? Number(lonSrc) : null;
    } else if (w.location_mode === 'teacher_home') {
      locationName = 'Teacher home';
      const parts = [w.teacher_street, w.teacher_city, w.teacher_state, w.teacher_zip, w.teacher_country]
        .map((x) => String(x || '').trim())
        .filter(Boolean);
      addressLine = parts.join(', ');
      lat = w.teacher_lat != null ? Number(w.teacher_lat) : null;
      lon = w.teacher_lon != null ? Number(w.teacher_lon) : null;
    } else if (w.location_mode === 'remote') {
      locationName = 'Remote';
      addressLine = 'Remote work';
    }

    const title = locationName ? `Work: ${workType} - ${locationName}` : `Work: ${workType}`;

    const dest = {
      name: locationName || 'Work',
      address: addressLine,
      lat,
      lon,
      latitude: lat,
      longitude: lon,
    };

    setWorkLocModal({
      open: true,
      title,
      addressLine,
      dest,
      teacher: null,
    });
  };

  const openNotesHistoryModal = async (studentId, studentName) => {
    setNotesHistoryLoading(true);
    setShowNotesHistory(true);
    setNotesHistoryStudent(studentName);
    try {
      const res = await API.get(`/students/${studentId}/notes-history`);
      setNotesHistory(res.data || []);
    } catch {
      setNotesHistory([]);
    } finally {
      setNotesHistoryLoading(false);
    }
  };

  useEffect(() => {
    API.get('/server-time')
      .then((res) => {
        const srv = res.data?.serverTime?.slice(0, 10);
        setServerDateStr(srv || null);
        if (srv) {
          setFilters((f) => {
            const { from, to } = lastNDaysRange(15, srv);
            return {
              ...f,
              dateFrom: f.dateFrom || from,
              dateTo: f.dateTo || to,
            };
          });
        }
      })
      .catch(() => setServerDateStr(null));

    fetchFilters();

    // org locations
    (async () => {
      try {
        const res = await API.get('/org-locations');
        const rows = res.data?.locations || [];
        const m = {};
        rows.forEach((r) => {
          if (r?.id) m[Number(r.id)] = r;
        });
        setOrgLocationsMap(m);
      } catch {
        setOrgLocationsMap({});
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchFilters = async () => {
    const t = await API.get('/teachers');
    setTeachers(t.data || []);
    const s = await API.get('/students/all');
    const sorted = (s.data || []).slice().sort((a, b) => {
      const la = (a.last_name || '').localeCompare(b.last_name || '', 'en', { sensitivity: 'base' });
      return la !== 0
        ? la
        : (a.first_name || '').localeCompare(b.first_name || '', 'en', { sensitivity: 'base' });
    });
    setStudents(sorted);
  };

  const fetchVisitsAndWork = async () => {
    if (!filters.dateFrom && !filters.dateTo) return;

    const params = {};
    if (filters.dateFrom) params.date_from = filters.dateFrom;
    if (filters.dateTo) params.date_to = filters.dateTo;
    if (filters.teacherId) params.teacher_id = filters.teacherId;
    if (filters.studentId) params.student_id = filters.studentId;
    if (filters.status) params.status = filters.status;

    const res = await API.get('/admin/visits', { params });
    let data = res.data || [];

    if (filters.search) {
      const term = filters.search.toLowerCase();
      data = data.filter(
        (v) =>
          (v.student_name && v.student_name.toLowerCase().includes(term)) ||
          (v.teacher_name && v.teacher_name.toLowerCase().includes(term))
      );
    }

    setVisits(data);

    // gsign log (visits only)
    if (data.length) {
      const ids = data.map((v) => v.visit_id).filter(Boolean).join(',');
      if (ids) {
        const logs = await API.get('/visit-gsign-log', { params: { visit_ids: ids } });
        setGsignLog(logs.data || {});
      } else {
        setGsignLog({});
      }
    } else {
      setGsignLog({});
    }

    // work blocks (range only if dateFrom+dateTo exist)
    if (filters.dateFrom && filters.dateTo) {
      let teacherIds = [];
      if (filters.teacherId) teacherIds = [String(filters.teacherId)];
      else teacherIds = (teachers || []).map((t) => String(t.id)).filter(Boolean);

      if (teacherIds.length) {
        try {
          const wRes = await API.get('/teacher-work/range', {
            params: {
              date_from: filters.dateFrom,
              date_to: filters.dateTo,
              teacher_ids: teacherIds.join(','),
            },
          });
          const w = normalizeTeacherWorkToUnified(wRes.data || []);
          setTeacherWork(w);
        } catch {
          setTeacherWork([]);
        }
      } else {
        setTeacherWork([]);
      }
    } else {
      setTeacherWork([]);
    }
  };

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  function getCurrentWeekRange(baseDateStr) {
    const base = parseYMDLocal(baseDateStr);
    const day = base.getDay() === 0 ? 7 : base.getDay();
    const monday = new Date(base);
    monday.setDate(base.getDate() - day + 1);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { monday: fmt(monday), sunday: fmt(sunday) };
  }

  const setQuickFilter = (type) => {
    if (!serverDateStr) return;

    if (type === 'today') {
      setFilters((f) => ({ ...f, dateFrom: serverDateStr, dateTo: serverDateStr }));
    } else if (type === 'week') {
      const { monday, sunday } = getCurrentWeekRange(serverDateStr);
      setFilters((f) => ({ ...f, dateFrom: monday, dateTo: sunday }));
    } else if (type === 'future') {
      const base = new Date(`${serverDateStr}T00:00:00`);
      const end = new Date(base);
      end.setDate(end.getDate() + 60);

      const pad = (n) => String(n).padStart(2, '0');
      const fmt2 = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

      setFilters((f) => ({ ...f, dateFrom: serverDateStr, dateTo: fmt2(end) }));
    }
  };

  useEffect(() => {
    if (!filters.dateFrom && !filters.dateTo) return;
    fetchVisitsAndWork();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.dateFrom,
    filters.dateTo,
    filters.teacherId,
    filters.studentId,
    filters.status,
    filters.search,
    teachers.length,
  ]);

  // unified list (visits + work)
  const unifiedItems = useMemo(() => {
    const merged = [...(visits || []), ...(teacherWork || [])];
    merged.sort(sortUnified);
    return merged;
  }, [visits, teacherWork]);

  const grouped = useMemo(() => groupByDateUnified(unifiedItems), [unifiedItems]);

  return (
    <div className="w-full px-1 sm:px-1">
      <h2 className="text-lg font-bold mb-4">All Scheduled Visits</h2>

      {/* FILTER CARD */}
      <div className="w-full bg-white rounded-lg shadow-sm p-3 sm:p-4 mb-4 flex flex-col gap-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          <div className="flex flex-col">
            <label className="text-xs mb-1">Date From</label>
            <input
              type="date"
              name="dateFrom"
              value={filters.dateFrom}
              onChange={handleChange}
              className="border p-2 rounded"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs mb-1">Date To</label>
            <input
              type="date"
              name="dateTo"
              value={filters.dateTo}
              onChange={handleChange}
              className="border p-2 rounded"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs mb-1">Teacher</label>
            <select name="teacherId" value={filters.teacherId} onChange={handleChange} className="border p-2 rounded">
              <option value="">All Teachers</option>
              {teachers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs mb-1">Student</label>
            <select name="studentId" value={filters.studentId} onChange={handleChange} className="border p-2 rounded">
              <option value="">All Students</option>
              {students.map((s) => (
                <option key={s.id} value={s.id}>
                  {(s.last_name || '').trim()}, {(s.first_name || '').trim()}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs mb-1">Status</label>
            <select name="status" value={filters.status} onChange={handleChange} className="border p-2 rounded">
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="complete">Complete</option>
              <option value="canceled">Canceled</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-xs mb-1">Search</label>
            <input
              type="text"
              name="search"
              value={filters.search}
              placeholder="Search student/teacher"
              onChange={handleChange}
              className="border p-2 rounded"
            />
          </div>
        </div>

        <button onClick={fetchVisitsAndWork} className="mt-2 bg-blue-600 text-white px-4 py-2 rounded w-full sm:w-auto">
          Apply Filters
        </button>
      </div>

      {/* Fast filters + CSV */}
      <div className="flex gap-2 mb-3 flex-wrap">
        <button onClick={() => setQuickFilter('today')} className="text-xs bg-gray-200 rounded px-2 py-1">
          Today
        </button>
        <button onClick={() => setQuickFilter('week')} className="text-xs bg-gray-200 rounded px-2 py-1">
          This Week
        </button>
        <button onClick={() => setQuickFilter('future')} className="text-xs bg-gray-200 rounded px-2 py-1">
          All Future
        </button>
        <button onClick={() => exportToCSV(visits)} className="text-xs bg-green-200 rounded px-2 py-1 ml-auto">
          Export CSV
        </button>
      </div>

      {/* LIST */}
      {Object.keys(grouped).length === 0 ? (
        <div className="text-gray-500 mt-6">No visits found for the selected criteria.</div>
      ) : !serverDateStr ? (
        <div className="p-4 text-gray-500 text-center">Loading server time...</div>
      ) : (
        Object.entries(grouped)
          .sort(([a], [b]) => b.localeCompare(a))
          .map(([date, group]) => (
            <div key={date} className="mb-6 shadow rounded-lg bg-white border">
              <div className="font-bold text-base px-4 py-3 bg-gray-50 rounded-t-lg flex items-center">
                <span>{date}</span>
                <span className="ml-3 bg-blue-200 text-blue-900 text-xs px-2 py-0.5 rounded-full">
                  {group.length} items
                </span>
              </div>

              {/* DESKTOP */}
              <div className="overflow-x-auto hidden sm:block">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="px-2 py-1">Time</th>
                      <th className="px-2 py-1">Student / Work</th>
                      <th className="px-2 py-1">Teacher</th>
                      <th className="px-2 py-1 w-40">Status</th>
                      <th className="px-2 py-1">Reassigned</th>
                      <th className="px-2 py-1">G-sign</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.map((v) => {
                      const work = isWorkItem(v);

                      const visitDateStr = String(v.scheduled_at || '').slice(0, 10);
                      let rowClass = 'hover:bg-gray-50';
                      if (compareDate(visitDateStr, serverDateStr) < 0) rowClass += ' opacity-60 bg-gray-100';
                      else if (compareDate(visitDateStr, serverDateStr) > 0) rowClass += ' opacity-90 bg-yellow-50';
                      else rowClass += ' bg-green-50';

                      // WORK ROW
                      if (work) {
                        const from = v.scheduled_time || parseHHMM(v.from_hhmm) || parseHHMM(v.scheduled_at);
                        const to =
                          v.to_hhmm ||
                          parseHHMM(v.end_time) ||
                          (v.event_end_time ? String(v.event_end_time).slice(0, 5) : '');
                        const workType = String(v.work_type || 'other');

                        const teacherName = resolveTeacherName(
                          v.teacher_name || v.teacher_full_name || v.teacher,
                          v.teacher_id
                        );

                        // ✅ work title: no " - Office" append; show location below if org_location
                        const titleText = `Work: ${workType}`;
                        const orgLocName = getOrgLocNameForWork(v, orgLocationsMap);

                        // ✅ canceled reason for work
                        const workCanceledReason = String(v.canceled_reason || '').trim();

                        return (
                          <tr key={`work_${v.work_id || v.id}`} className={rowClass}>
                            <td className="px-2 py-1">
                              <VisitTimeType
                                scheduled_at={v.scheduled_at}
                                scheduled_time={from}
                                event_end_time={to ? `${String(to).slice(0, 5)}:00` : null}
                              />
                            </td>

                            <td className="px-2 py-1">
                              <div className="flex flex-col">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <Building2 className="w-4 h-4 text-slate-600" />
                                  <span className="font-semibold text-sky-900">{titleText}</span>

                                  {/* ✅ book icon -> work notes modal */}
                                  <button
                                    onClick={() => openWorkNotesModal(v)}
                                    className="text-purple-700 hover:text-purple-900"
                                    title="Show work notes"
                                  >
                                    <FaBookOpen size={18} />
                                  </button>

                                  {/* map pin */}
                                  <button
                                    onClick={() => openWorkLocationModal(v)}
                                    className="text-gray-400 hover:text-blue-600"
                                    title="Show work location on map"
                                  >
                                    <MapPin size={18} />
                                  </button>
                                </div>

                                {/* ✅ location below title (like Clinic for visits) */}
                                {v.location_mode === 'org_location' ? (
                                  <div className="mt-0.5 text-[11px] text-slate-500">
                                    {orgLocName ? (
                                      <>
                                        <span className="font-semibold">Location:</span> {orgLocName}
                                      </>
                                    ) : (
                                      <span className="italic">Location</span>
                                    )}
                                  </div>
                                ) : null}
                              </div>
                            </td>

                            <td className="px-2 py-1">
                              <div>{teacherName}</div>
                            </td>

                            <td className="px-2 py-1 capitalize">
                              <StatusBadge status={v.status}>{v.status}</StatusBadge>

                              {/* ✅ canceled reason for work */}
                              {v.status === 'canceled' && workCanceledReason ? (
                                <div className="text-xs text-purple-700 mt-1 italic">{workCanceledReason}</div>
                              ) : null}
                            </td>

                            <td className="px-2 py-1 text-xs text-gray-400">N/A</td>
                            <td className="px-2 py-1 text-center text-xs text-gray-400">N/A</td>
                          </tr>
                        );
                      }

                      // VISIT ROW
                      const isEvent = isEventVisit(v);

                      const locMode = String(v.location_mode || 'at_home').toLowerCase();
                      const locIcon =
                        locMode === 'at_clinic' ? (
                          <Building2 className="w-4 h-4 text-slate-600" />
                        ) : (
                          <Home className="w-4 h-4 text-slate-600" />
                        );

                      const clinicName = getClinicNameForVisit(v, orgLocationsMap);

                      // g-sign logic
                      const gsign = gsignLog[v.visit_id];
                      const hasGsign = gsign && gsign.lat != null && gsign.lon != null;
                      const gLat2 = hasGsign ? round2(gsign.lat) : null;
                      const gLon2 = hasGsign ? round2(gsign.lon) : null;
                      const checkinLat2 = v.checkin_lat != null ? round2(v.checkin_lat) : null;
                      const checkinLon2 = v.checkin_lon != null ? round2(v.checkin_lon) : null;
                      const checkoutLat2 = v.checkout_lat != null ? round2(v.checkout_lat) : null;
                      const checkoutLon2 = v.checkout_lon != null ? round2(v.checkout_lon) : null;

                      let gColor = 'text-gray-400';
                      if (hasGsign) {
                        const checkinMatch =
                          checkinLat2 != null &&
                          checkinLon2 != null &&
                          gLat2 === checkinLat2 &&
                          gLon2 === checkinLon2;
                        const checkoutMatch =
                          checkoutLat2 != null &&
                          checkoutLon2 != null &&
                          gLat2 === checkoutLat2 &&
                          gLon2 === checkoutLon2;
                        if (checkinMatch || checkoutMatch) gColor = 'text-green-700';
                        else gColor = 'text-red-600';
                      }

                      return (
                        <tr key={v.visit_id} className={rowClass}>
                          <td className="px-2 py-1">
                            <VisitTimeType scheduled_at={v.scheduled_at} event_end_time={v.event_end_time} />
                          </td>

                          <td className="px-2 py-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              {locIcon}
                              <span
                                onClick={() => openStudentModal(v.student_id)}
                                className="text-blue-700 underline cursor-pointer hover:text-blue-900"
                                title="Student Details"
                              >
                                {v.student_name}
                              </span>

                              {isEvent ? (
                                <EventBadge isEvent={true} />
                              ) : (
                                <ProgramBadge program_type={v.program_type} discipline_code={v.discipline_code} />
                              )}

                              <button
                                onClick={() => openMapModalVisit(v)}
                                className="ml-2 text-gray-400 hover:text-blue-600"
                                title="Show on Map"
                              >
                                <MapPin size={18} />
                              </button>

                              <button
                                onClick={() => openNotesHistoryModal(v.student_id, v.student_name)}
                                className="ml-2 text-purple-700 hover:text-purple-900"
                                title="Show Notes History"
                              >
                                <FaBookOpen size={18} />
                              </button>
                            </div>

                            {locMode === 'at_clinic' ? (
                              <div className="mt-0.5 text-[11px] text-slate-500">
                                {clinicName ? (
                                  <>
                                    <span className="font-semibold">Clinic:</span> {clinicName}
                                  </>
                                ) : (
                                  <span className="italic">Clinic</span>
                                )}
                              </div>
                            ) : null}
                          </td>

                          <td className="px-2 py-1">
                            <div>{v.teacher_name}</div>
                            {v.trainee_teacher_name && (
                              <div className="text-xs text-emerald-700 mt-0.5">Trainee: {v.trainee_teacher_name}</div>
                            )}
                            {v.teacher_id !== v.original_teacher_id && v.original_teacher_name && (
                              <div className="text-xs text-gray-400 mt-1">Default: {v.original_teacher_name}</div>
                            )}
                          </td>

                          <td className="px-2 py-1 capitalize">
                            <StatusBadge status={v.status}>{v.status}</StatusBadge>

                            {v.status === 'canceled' && v.notes && (
                              <div className="text-xs text-purple-700 mt-1 italic">{v.notes}</div>
                            )}

                            {v.trainee_teacher_id && (
                              <div className="mt-1">
                                <SupervisedBadge />
                              </div>
                            )}
                          </td>

                          <td className="px-2 py-1 text-xs text-gray-400">
                            {v.is_reassigned ? (
                              <StatusBadge status={v.reassigned_status}>{v.reassigned_status}</StatusBadge>
                            ) : (
                              'Default Teacher'
                            )}
                          </td>

                          <td className="px-2 py-1 text-center">
                            {v.g_sign_confirmed ? (
                              <div className="flex flex-col items-center">
                                <div className="flex flex-row items-center gap-1">
                                  <span className="text-green-600 text-md">✅</span>
                                  {hasGsign && (
                                    <button
                                      title="Show confirmation location"
                                      className={gColor}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setGsignMapModal({
                                          open: true,
                                          position: { lat: Number(gsign.lat), lon: Number(gsign.lon) },
                                          date: gsign.confirmed_at,
                                        });
                                      }}
                                    >
                                      <MapPin size={16} />
                                    </button>
                                  )}
                                </div>
                                {gsign && (
                                  <span className={`text-xs mt-0.5 ${gColor}`}>
                                    {gsign.confirmed_at?.slice(0, 16).replace('T', ' ')}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-red-600 text-md">❌</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* MOBILE */}
              <div className="sm:hidden flex flex-col gap-2 p-2">
                {group.map((v) => {
                  const work = isWorkItem(v);
                  const visitDateStr = String(v.scheduled_at || '').slice(0, 10);

                  let rowClass = 'rounded-2xl border shadow p-3 mb-1 ';
                  if (compareDate(visitDateStr, serverDateStr) < 0) rowClass += ' opacity-60 bg-gray-100';
                  else if (compareDate(visitDateStr, serverDateStr) > 0) rowClass += ' opacity-90 bg-yellow-50';
                  else rowClass += ' bg-green-50';

                  // WORK card
                  if (work) {
                    const from = v.scheduled_time || parseHHMM(v.from_hhmm) || parseHHMM(v.scheduled_at);
                    const to =
                      v.to_hhmm ||
                      parseHHMM(v.end_time) ||
                      (v.event_end_time ? String(v.event_end_time).slice(0, 5) : '');
                    const workType = String(v.work_type || 'other');

                    const teacherName = resolveTeacherName(
                      v.teacher_name || v.teacher_full_name || v.teacher,
                      v.teacher_id
                    );

                    const titleText = `Work: ${workType}`;
                    const orgLocName = getOrgLocNameForWork(v, orgLocationsMap);
                    const workCanceledReason = String(v.canceled_reason || '').trim();

                    return (
                      <div key={`work_${v.work_id || v.id}`} className={rowClass}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex-1 flex items-center gap-2 flex-wrap">
                            <Building2 className="w-4 h-4 text-slate-600" />
                            <span className="font-semibold text-sky-900">{titleText}</span>

                            <button
                              onClick={() => openWorkNotesModal(v)}
                              className="text-purple-700 hover:text-purple-900"
                              title="Show work notes"
                            >
                              <FaBookOpen size={16} />
                            </button>

                            <button
                              onClick={() => openWorkLocationModal(v)}
                              className="text-gray-400 hover:text-blue-600"
                              title="Show on Map"
                            >
                              <MapPin size={16} />
                            </button>
                          </div>

                          <div className="flex-1 text-right">
                            <VisitTimeType
                              scheduled_at={v.scheduled_at}
                              scheduled_time={from}
                              event_end_time={to ? `${String(to).slice(0, 5)}:00` : null}
                            />
                          </div>
                        </div>

                        {v.location_mode === 'org_location' ? (
                          <div className="mt-1 text-[11px] text-slate-500">
                            {orgLocName ? (
                              <>
                                <span className="font-semibold">Location:</span> {orgLocName}
                              </>
                            ) : (
                              <span className="italic">Location</span>
                            )}
                          </div>
                        ) : null}

                        <div className="flex flex-wrap gap-2 items-center mt-2">
                          <StatusBadge status={v.status}>{v.status}</StatusBadge>
                          {v.status === 'canceled' && workCanceledReason ? (
                            <div className="w-full text-xs text-purple-700 italic">{workCanceledReason}</div>
                          ) : null}
                          <span className="text-xs text-gray-400">G-sign: N/A</span>
                        </div>

                        <div className="grid grid-cols-2 gap-2 mt-3">
                          <div>
                            <span className="block text-xs text-gray-600">Teacher</span>
                            <span>{teacherName}</span>
                          </div>
                          <div>
                            <span className="block text-xs text-gray-600">G-sign</span>
                            <span className="text-xs text-gray-400">N/A</span>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // VISIT card
                  const isEvent = isEventVisit(v);

                  const locMode = String(v.location_mode || 'at_home').toLowerCase();
                  const locIcon =
                    locMode === 'at_clinic' ? (
                      <Building2 className="w-4 h-4 text-slate-600" />
                    ) : (
                      <Home className="w-4 h-4 text-slate-600" />
                    );
                  const clinicName = getClinicNameForVisit(v, orgLocationsMap);

                  const gsign = gsignLog[v.visit_id];
                  const hasGsign = gsign && gsign.lat != null && gsign.lon != null;

                  return (
                    <div key={v.visit_id} className={rowClass}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex-1 flex items-center gap-2 flex-wrap">
                          {locIcon}
                          <span
                            onClick={() => openStudentModal(v.student_id)}
                            className="text-blue-700 underline cursor-pointer hover:text-blue-900"
                          >
                            {v.student_name}
                          </span>
                          {isEvent ? (
                            <EventBadge isEvent={true} />
                          ) : (
                            <ProgramBadge program_type={v.program_type} discipline_code={v.discipline_code} />
                          )}
                          <button
                            onClick={() => openMapModalVisit(v)}
                            className="ml-1 text-gray-400 hover:text-blue-600"
                            title="Show on Map"
                          >
                            <MapPin size={16} />
                          </button>
                          <button
                            onClick={() => openNotesHistoryModal(v.student_id, v.student_name)}
                            className="ml-1 text-purple-700 hover:text-purple-900"
                            title="Show Notes History"
                          >
                            <FaBookOpen size={16} />
                          </button>
                        </div>
                        <div className="flex-1 text-right">
                          <VisitTimeType scheduled_at={v.scheduled_at} event_end_time={v.event_end_time} />
                        </div>
                      </div>

                      {locMode === 'at_clinic' ? (
                        <div className="mt-1 text-[11px] text-slate-500">
                          {clinicName ? (
                            <>
                              <span className="font-semibold">Clinic:</span> {clinicName}
                            </>
                          ) : (
                            <span className="italic">Clinic</span>
                          )}
                        </div>
                      ) : null}

                      <div className="flex flex-wrap gap-2 items-center mt-2">
                        <StatusBadge status={v.status}>{v.status}</StatusBadge>
                        {v.status === 'canceled' && v.notes ? (
                          <div className="w-full text-xs text-purple-700 italic">{v.notes}</div>
                        ) : null}
                        {v.trainee_teacher_id && <SupervisedBadge />}
                      </div>

                      <div className="grid grid-cols-2 gap-2 mt-3">
                        <div>
                          <span className="block text-xs text-gray-600">Teacher</span>
                          <span>{v.teacher_name}</span>
                        </div>
                        <div>
                          <span className="block text-xs text-gray-600">G-sign</span>
                          {v.g_sign_confirmed ? (
                            <div className="flex items-center gap-1">
                              <span className="text-green-600 text-md">✅</span>
                              {hasGsign && (
                                <button
                                  className="text-slate-500"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setGsignMapModal({
                                      open: true,
                                      position: { lat: Number(gsign.lat), lon: Number(gsign.lon) },
                                      date: gsign.confirmed_at,
                                    });
                                  }}
                                >
                                  <MapPin size={16} />
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="text-red-600 text-md">❌</span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
      )}

      {/* Modals */}
      <StudentDetailsModal
        open={studentDetailsModal.open}
        onClose={() => setStudentDetailsModal({ open: false, student: null })}
        student={studentDetailsModal.student}
      />

      <MapModal
        open={mapModal.open}
        onClose={() => setMapModal({ open: false, student: null, teacher: null })}
        student={mapModal.student}
        teacher={mapModal.teacher}
      />

      <WorkLocationModal
        open={!!workLocModal.open}
        onClose={() => setWorkLocModal({ open: false, title: '', addressLine: '', dest: null, teacher: null })}
        title={workLocModal.title}
        addressLine={workLocModal.addressLine}
        dest={workLocModal.dest}
        teacher={workLocModal.teacher}
      />

      <SimpleMapModal
        open={gsignMapModal.open}
        onClose={() => setGsignMapModal({ open: false, position: null, date: null })}
        position={gsignMapModal.position}
        label={`Parent confirmed location${
          gsignMapModal.date ? ` at ${gsignMapModal.date.slice(0, 16).replace('T', ' ')}` : ''
        }`}
      />

      {/* ✅ Work Notes Modal */}
      {workNotesModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded shadow-lg max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start gap-3 mb-3">
              <div className="min-w-0">
                <h3 className="text-lg font-bold truncate">
                  Work Notes — {workNotesModal.workType}
                </h3>
                <div className="text-xs text-gray-500 mt-1">
                  {workNotesModal.teacherName ? `Teacher: ${workNotesModal.teacherName}` : null}
                  {workNotesModal.scheduledAt ? ` • ${String(workNotesModal.scheduledAt).slice(0, 16).replace('T', ' ')}` : null}
                </div>
              </div>
              <button className="text-gray-500" onClick={() => setWorkNotesModal((s) => ({ ...s, open: false }))}>
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div className="border rounded p-3 bg-gray-50">
                <div className="text-xs text-gray-500 mb-1">Title</div>
                <div className="font-semibold text-slate-800">
                  {workNotesModal.workTitle || <span className="italic text-gray-400">No title</span>}
                </div>
              </div>

              <div className="border rounded p-3 bg-gray-50">
                <div className="text-xs text-gray-500 mb-1">Notes</div>
                <div className="text-sm text-slate-800 whitespace-pre-wrap">
                  {workNotesModal.notes || <span className="italic text-gray-400">No notes</span>}
                </div>
              </div>
            </div>

            <div className="flex justify-end mt-4">
              <button
                onClick={() => setWorkNotesModal((s) => ({ ...s, open: false }))}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notes history modal (isti kao pre) */}
      {showNotesHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold">Notes History: {notesHistoryStudent}</h3>
              <button className="text-gray-500" onClick={() => setShowNotesHistory(false)}>
                ✕
              </button>
            </div>
            {notesHistoryLoading ? (
              <div className="p-4 text-center text-gray-400">Loading...</div>
            ) : notesHistory.length === 0 ? (
              <div className="p-4 text-center text-gray-500">No notes found for this student.</div>
            ) : (
              <div className="space-y-4">
                {notesHistory.map((entry, idx) => (
                  <div key={idx} className="border rounded p-3 bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex flex-col">
                        <span className="font-semibold text-blue-900">{entry.teacher_name}</span>
                        {entry.discipline_name && <span className="text-xs text-gray-500">{entry.discipline_name}</span>}
                      </div>
                      <span className="text-xs text-gray-500">
                        {entry.scheduled_at ? entry.scheduled_at.slice(0, 16).replace('T', ' ') : ''}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      {entry.domains && entry.domains.length > 0 ? (
                        entry.domains.map((dom) =>
                          dom.note && dom.note.trim() !== '' ? (
                            <div key={dom.domain_id}>
                              <span className="font-semibold">{dom.name || dom.code || 'Domain'}:</span>{' '}
                              <span>{dom.note}</span>
                            </div>
                          ) : null
                        )
                      ) : (
                        <span className="text-xs text-gray-500 italic">No domain notes for this visit.</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminVisits;













